import type { FC } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button } from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import {
  CreateFavoriteContentMutation,
  DeleteFavoriteContentMutation,
  FavoriteContentQuery,
} from "@/entities/contents";
import { useUser } from "@/shared/hooks/use-user/useUser";
import { useTranslation } from "@/shared/hooks";
import type {
  CreateFavoriteContentMutationType,
  DeleteFavoriteContentMutationType,
  FavoriteContentQueryType,
} from "@/types/graphql/site.generated";

// Порт референсного arena/components/FavoriteButton: «Сохранить»/«Сохранено»,
// клик неавторизованного открывает диалог входа.
export const FavoriteButton: FC<{ contentId: string }> = ({ contentId }) => {
  const { user, openSignIn } = useUser();
  const { t } = useTranslation();

  const { data } = useQuery<FavoriteContentQueryType>(FavoriteContentQuery, {
    variables: { contentId },
    skip: !user,
  });
  const isFavorite = Boolean(data?.favoriteContent);

  const [createFavoriteContent] =
    useMutation<CreateFavoriteContentMutationType>(
      CreateFavoriteContentMutation,
      {
        variables: { contentId },
        update(cache) {
          cache.writeQuery({
            query: FavoriteContentQuery,
            variables: { contentId },
            data: { favoriteContent: true },
          });
        },
      },
    );
  const [deleteFavoriteContent] =
    useMutation<DeleteFavoriteContentMutationType>(
      DeleteFavoriteContentMutation,
      {
        variables: { contentId },
        update(cache) {
          cache.writeQuery({
            query: FavoriteContentQuery,
            variables: { contentId },
            data: { favoriteContent: false },
          });
        },
      },
    );

  const onClick = () => {
    if (!user) return openSignIn();
    if (isFavorite) void deleteFavoriteContent();
    else void createFavoriteContent();
  };

  return (
    <Button
      variant="outlined"
      color="inherit"
      startIcon={
        isFavorite ? (
          <BookmarkIcon fontSize="small" color="primary" />
        ) : (
          <BookmarkBorderIcon fontSize="small" color="inherit" />
        )
      }
      size="small"
      onClick={onClick}
      sx={{ whiteSpace: "nowrap" }}
    >
      {isFavorite ? t("video.actions.saved") : t("video.actions.save")}
    </Button>
  );
};
